import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ability from '../../../ability';
import { commonHandlePageChange, commonHandleSorting } from '../../../utils/commonFunctions';
import { Common } from '../../../utils/constants';
import { handleErrorMessage } from '../../../utils/helper';
import CommonService from '../../../services/common/common-service';
import DriverService from '../../../services/driver/driver-service';
import { IcheckReportStatus, IDriverDebtorLetters } from '../../../models/driver';
import { IRunHistoryDetails, IRunHistoryList } from '../../../models/driver-debt';
import StandardResponse from '../../../models/standard-response';
import { IregenerateLetterModalType } from '../../../types/stateTypes/stateTypes';
import { IDriverDebtContentProps } from '../../../types/propTypes';

export const useDriverDebtContent = (props: IDriverDebtContentProps) => {
    const [s1Days, setS1Days] = useState<number>(0);
    const [s2Days, setS2Days] = useState<number>(0);
    const [s1Letters, setS1Letters] = useState<Array<IDriverDebtorLetters>>([]);
    const [s2Letters, setS2Letters] = useState<Array<IDriverDebtorLetters>>([]);
    const [s1Letter, setS1Letter] = useState<Record<string, any> | null>(null);
    const [s2Letter, setS2Letter] = useState<Record<string, any> | null>(null);
    const [status, setStatus] = useState<IcheckReportStatus>({ STRIKE_ONE: null, STRIKE_TWO: null });
    const [runHistoryList, setRunHistoryList] = useState<Array<IRunHistoryDetails>>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [pastLettersList, setPastLettersList] = useState<Array<IRunHistoryDetails>>([]);
    const [plTotalCount, setPlTotalCount] = useState<number>(0);
    const [regenerateLetterModal, setRegenerateLetterModal] = useState<IregenerateLetterModalType>({
        showModal: false,
        runHistoryDetails: null,
    });

    const commonService = new CommonService();
    const driverService = new DriverService();

    const filterState = props.filterState;
    const pastLettersFilterState = props.pastLettersFilterState;

    const fetchData = () => {
        if (ability.can(Common.Actions.GENERATE_CHASE_REPORT, Common.Modules.DRIVERS.DRIVER_DEBT)) {
            getConfigData(Common.LookupCode.BAD_DEBT_STRIKE_ONE_DEBT_AGE).then((res) => setS1Days(+res));
            getConfigData(Common.LookupCode.BAD_DEBT_STRIKE_TWO_DEBT_AGE).then((res) => setS2Days(+res));
            getStatus();
        }
        if (
            ability.can(Common.Actions.GENERATE_CHASE_REPORT, Common.Modules.DRIVERS.DRIVER_DEBT) ||
            ability.can(Common.Actions.REGENERATE_DEBT_LETTER, Common.Modules.DRIVERS.DRIVER_DEBT)
        ) {
            getTemplates();
        }
    };

    const getRunHistory = async () => {
        let runHistoryList: Array<IRunHistoryDetails> = [];
        const filterInput = {
            limit: filterState.rowsPerPage,
            offset: filterState.offset,
            order: [[filterState.sortColumn, filterState.sortOrder]],
        };
        await driverService
            .getRunHistoryList(filterInput)
            .then(async (res: StandardResponse<IRunHistoryList>) => {
                runHistoryList = res.Data.rows;
                setTotalCount(res.Data.count);
            })
            .catch((m) => {
                handleErrorMessage(m);
            });
        setRunHistoryList(runHistoryList);
    };

    const getPastLettersList = async () => {
        let pastLettersList: Array<IRunHistoryDetails> = [];
        const filterInput = {
            limit: pastLettersFilterState.rowsPerPage,
            offset: pastLettersFilterState.offset,
            order: [[pastLettersFilterState.sortColumn, pastLettersFilterState.sortOrder]],
        };
        await driverService
            .getPastDriverDebtChaseLetters(filterInput)
            .then(async (res: StandardResponse<IRunHistoryList>) => {
                pastLettersList = res.Data.rows;
                setPlTotalCount(res.Data.count);
            })
            .catch((m) => {
                handleErrorMessage(m);
            });

        setPastLettersList(pastLettersList);
    };

    useEffect(() => {
        getRunHistory();
    }, [filterState]);

    useEffect(() => {
        getPastLettersList();
    }, [pastLettersFilterState]);

    useEffect(() => {
        fetchData();
    }, []);

    const startGeneration = async (type: string) => {
        let template_id = -1;
        if (type === Common.LookupCode.STRIKE_ONE) {
            if (s1Letter && s1Letter.id) template_id = s1Letter.id;
        } else if (type === Common.LookupCode.STRIKE_TWO) {
            if (s2Letter && s2Letter.id) template_id = s2Letter.id;
        }
        if (template_id > -1) {
            await driverService
                .generateDriverDebtChaseLetters(type, template_id)
                .then(async (res: StandardResponse<any>) => {
                    if (res.Messages) {
                        toast.success(res.Messages);
                    }
                })
                .catch((m) => {
                    handleErrorMessage(m);
                });
            getStatus();
            await getRunHistory();
            await getPastLettersList();
        }
    };

    const getStatus = async () => {
        await driverService.getDriverDebtLettersGenerationStatus().then((res) => {
            setStatus(res.Data);
        });
    };

    const getTemplates = async () => {
        driverService.getDriverDebtLetters({ status: [Common.ENABLE] }).then((res) => {
            const s1Letters = res.Data.rows
                .filter((d) => d.driver_debt_report_type.code === Common.LookupCode.STRIKE_ONE)
                .map((item) => {
                    item = Object.assign(item, { label: item.name, value: item.id });
                    return item;
                });
            const s2Letters = res.Data.rows
                .filter((d) => d.driver_debt_report_type.code === Common.LookupCode.STRIKE_TWO)
                .map((item) => {
                    item = Object.assign(item, { label: item.name, value: item.id });
                    return item;
                });
            const s1Letter = res.Data.rows.find(
                (d) => d.driver_debt_report_type.code === Common.LookupCode.STRIKE_ONE && d.is_default === true,
            );
            if (s1Letter) {
                s1Letter.label = s1Letter.name;
                s1Letter.value = s1Letter.id;
            }
            const s2Letter = res.Data.rows.find(
                (d) => d.driver_debt_report_type.code === Common.LookupCode.STRIKE_TWO && d.is_default === true,
            );
            if (s2Letter) {
                s2Letter.label = s2Letter.name;
                s2Letter.value = s2Letter.id;
            }
            setS1Letters(s1Letters);
            setS2Letters(s2Letters);
            setS1Letter(s1Letter);
            setS2Letter(s2Letter);
        });
    };

    const getConfigData = async (code: string) => {
        let configData: Array<any> = [];
        await commonService
            .getConfiguration({ code })
            .then(async (res) => {
                configData = res.data;
            })
            .catch((m) => {
                toast.error(m.toString());
            });
        if (configData.length > 0 && configData[0] && configData[0].value) {
            return configData[0].value;
        }
        return null;
    };

    const getFileName = (filePath: string) => {
        return filePath.split('/')[filePath.split('/').length - 1];
    };

    const handlePageChange = async (currentPage: number, rowsPerPage: number) => {
        commonHandlePageChange(currentPage, rowsPerPage, props.updateFilterState);
    };

    const handlePastLettersPageChange = async (plCurrentPage: number, plRowsPerPage: number) => {
        commonHandlePageChange(plCurrentPage, plRowsPerPage, props.updatePLFilterState);
    };

    const handleSorting = async (columnName: string) => {
        commonHandleSorting(columnName, filterState, props.updateFilterState);
    };

    const handlePastLettersSorting = async (columnName: string) => {
        commonHandleSorting(columnName, pastLettersFilterState, props.updatePLFilterState);
    };

    const manageRegenerateLetterModal = async (
        showModal: boolean,
        runHistoryDetails: IRunHistoryDetails | null = null,
    ) => {
        setRegenerateLetterModal({ showModal, runHistoryDetails });
    };

    const refreshPastLettersList = async () => {
        await getPastLettersList();
    };

    return {
        s1Days,
        s2Days,
        s1Letters,
        s2Letters,
        s1Letter,
        s2Letter,
        status,
        runHistoryList,
        totalCount,
        pastLettersList,
        plTotalCount,
        regenerateLetterModal,
        setS1Letter,
        setS2Letter,
        fetchData,
        getRunHistory,
        getPastLettersList,
        startGeneration,
        getTemplates,
        getStatus,
        getConfigData,
        getFileName,
        handlePageChange,
        handlePastLettersPageChange,
        handleSorting,
        handlePastLettersSorting,
        manageRegenerateLetterModal,
        refreshPastLettersList,
    };
};
